import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { BulkSupportTicketAction } from "@redotech/effect-api-shared/models/shopper-ai/conversation/bulk-support-ticket-action";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { useState } from "react";
import { catchApiErrors } from "../db/error-handling";
import { invalidateSupportTicketListQueriesAfterDelay } from "../invalidate-support-ticket-list-queries";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export function useBulkSupportTicketAction(): {
  pendingAction: BulkSupportTicketAction | null;
  startBulkSupportTicketAction: (
    input:
      | {
          action: BulkSupportTicketAction;
          supportTicketIds: readonly SupportTicketId[];
        }
      | {
          action: BulkSupportTicketAction;
          excludedSupportTicketIds: readonly SupportTicketId[];
          filterGroup: string | undefined;
        },
  ) => Promise<void>;
} {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const [pendingAction, setPendingAction] =
    useState<BulkSupportTicketAction | null>(null);

  const startBulkSupportTicketAction = useHandler(
    async ({
      action,
      ...scope
    }:
      | {
          action: BulkSupportTicketAction;
          supportTicketIds: readonly SupportTicketId[];
        }
      | {
          action: BulkSupportTicketAction;
          filterGroup: string | undefined;
          excludedSupportTicketIds: readonly SupportTicketId[];
        }) => {
      if ("supportTicketIds" in scope && scope.supportTicketIds.length === 0) {
        return;
      }

      setPendingAction(action);
      try {
        const supportTicketClient = effectApi.getClient().supportTicket;
        if ("supportTicketIds" in scope) {
          await effectApi.runPromise(
            catchApiErrors(
              supportTicketClient.startBulkSupportTicketAction({
                payload: {
                  action,
                  supportTicketIds: [...scope.supportTicketIds],
                },
              }),
            ),
          );
        } else {
          await effectApi.runPromise(
            catchApiErrors(
              supportTicketClient.startBulkSupportTicketActionByFilter({
                payload: {
                  action,
                  excludedSupportTicketIds: [...scope.excludedSupportTicketIds],
                  filterGroup: scope.filterGroup,
                },
              }),
            ),
          );
        }
        invalidateSupportTicketListQueriesAfterDelay({
          queryClient: reactQueryClient,
          queryScope: collections.supportTicketsList.queryScope,
        });
      } catch {
        toast("Failed to start bulk action. Please try again.", {
          variant: "error",
        });
      } finally {
        setPendingAction(null);
      }
    },
  );

  return { pendingAction, startBulkSupportTicketAction };
}
